// AgpOverview.tsx
import {
  Box,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  Header,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Agp } from '../api/agp';
import ExcisePaymentModal from '../components/ExcisePaymentModal';
import ReleaseModal from '../components/modules/ReleaseModal';
import PeriodFilterControls from '../components/PeriodFilterControls';
import { useAuthContext } from '../contexts/AuthContext';
import { Batch } from '../models/Batch';
import { Recipe } from '../models/Recipe';

// Pure logic
import RecentMovementsTable from '../modules/Agp/RecentMovementsTable';
import ReleasesTable from '../modules/Agp/ReleasesTable';
import StockByProductTable from '../modules/Agp/StockByProductTable';
import {
  Movement,
  ProductRow,
  ViewMode,
  computeAlerts,
  computeExciseForMovement,
  computeKpis,
  computeTotalExcise,
  getPeriodBounds,
  groupMovementsByProduct,
  mapAgpToMovements,
} from './AgpOverview.logic';

type RefetchAllResult = {
  agp: Agp[];
  batchMap?: Record<string, Batch>;
  recipeMap?: Record<string, Recipe>;
};

type Props = {
  /** Initial data (server/render parent fetch) */
  agp: Agp[];
  batchMap: Record<string, Batch>;
  recipeMap: Record<string, Recipe>;

  /**
   * Optional fetchers so the page can "refresh" using the same code that provided initial props.
   * You can pass EITHER refetchAll OR the granular ones.
   */
  refetchAll?: () => Promise<RefetchAllResult>;
  refetchAgp?: () => Promise<Agp[]>;
  refetchBatchMap?: () => Promise<Record<string, Batch>>;
  refetchRecipeMap?: () => Promise<Record<string, Recipe>>;
};

const AgpOverview: FC<Props> = ({
  agp,
  batchMap,
  recipeMap,
  refetchAll,
  refetchAgp,
  refetchBatchMap,
  refetchRecipeMap,
}) => {
  const { tier } = useAuthContext();
  const navigate = useNavigate();

  const [voidModal, setVoidModal] = useState(false);

  // ---------- Local working copies so refresh can update UI ----------
  const [agpData, setAgpData] = useState<Agp[]>(agp);
  const [batchData, setBatchData] = useState<Record<string, Batch>>(batchMap);
  const [recipeData, setRecipeData] = useState<Record<string, Recipe>>(recipeMap);

  // keep in sync if parent changes props
  useEffect(() => setAgpData(agp), [agp]);
  useEffect(() => setBatchData(batchMap), [batchMap]);
  useEffect(() => setRecipeData(recipeMap), [recipeMap]);

  // ---------- UI state ----------
  const [releaseModalVisible, setReleaseModalVisible] = useState(false);
  const [exciseModalVisible, setExciseModalVisible] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [activeDate, setActiveDate] = useState<Date>(new Date());

  // per-table loading spinners
  const [stockLoading, setStockLoading] = useState(false);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [movementsLoading, setMovementsLoading] = useState(false);

  // ---------- Refresh helpers (reuse initial fetch) ----------
  // You can pass just `refetchAll`, or granular ones (agp/batch/recipe).
  const refreshAll = async () => {
    // Toggle all spinners since all tables depend on similar data
    setStockLoading(true);
    setReleasesLoading(true);
    setMovementsLoading(true);
    try {
      if (refetchAll) {
        const res = await refetchAll();
        if (res.agp) setAgpData(res.agp);
        if (res.batchMap) setBatchData(res.batchMap);
        if (res.recipeMap) setRecipeData(res.recipeMap);
      } else {
        // Fallback to granular fetchers or no-op if not provided
        const [agpNew, batchNew, recipeNew] = await Promise.all([
          refetchAgp ? refetchAgp() : Promise.resolve<Agp[]>(agpData),
          refetchBatchMap ? refetchBatchMap() : Promise.resolve<Record<string, Batch>>(batchData),
          refetchRecipeMap ? refetchRecipeMap() : Promise.resolve<Record<string, Recipe>>(recipeData),
        ]);

        setAgpData(agpNew);
        setBatchData(batchNew);
        setRecipeData(recipeNew);
      }
    } finally {
      setStockLoading(false);
      setReleasesLoading(false);
      setMovementsLoading(false);
    }
  };

  const refreshStock = async () => {
    setStockLoading(true);
    try {
      if (refetchAll) {
        const res = await refetchAll();
        setAgpData(res.agp ?? agpData);
        if (res.batchMap) setBatchData(res.batchMap);
        if (res.recipeMap) setRecipeData(res.recipeMap);
      } else if (refetchAgp) {
        setAgpData(await refetchAgp());
      } else {
        // no fetcher passed → no-op
      }
    } finally {
      setStockLoading(false);
    }
  };

  const refreshReleases = async () => {
    setReleasesLoading(true);
    try {
      if (refetchAll) {
        const res = await refetchAll();
        setAgpData(res.agp ?? agpData);
        if (res.batchMap) setBatchData(res.batchMap);
        if (res.recipeMap) setRecipeData(res.recipeMap);
      } else if (refetchAgp) {
        setAgpData(await refetchAgp());
      }
    } finally {
      setReleasesLoading(false);
    }
  };

  const refreshMovements = async () => {
    setMovementsLoading(true);
    try {
      if (refetchAll) {
        const res = await refetchAll();
        setAgpData(res.agp ?? agpData);
        if (res.batchMap) setBatchData(res.batchMap);
        if (res.recipeMap) setRecipeData(res.recipeMap);
      } else if (refetchAgp) {
        setAgpData(await refetchAgp());
      }
    } finally {
      setMovementsLoading(false);
    }
  };

  // Period navigation (show releases spinner while recomputing/refetching)
  const handlePeriodChange = (dir: 'prev' | 'next') => {
    const delta = dir === 'prev' ? -1 : 1;
    setReleasesLoading(true);
    setActiveDate((cur) => {
      const nd = new Date(cur);
      if (viewMode === 'month') {
        nd.setMonth(nd.getMonth() + delta, 1);
      } else if (viewMode === 'quarter') {
        nd.setMonth(nd.getMonth() + delta * 3, 1);
      } else {
        nd.setFullYear(nd.getFullYear() + delta);
        nd.setMonth(0, 1);
      }
      return nd;
    });
    // If you want to refetch on period change, call refreshReleases() here:
    // void refreshReleases();
    // If not, just flip off after this tick (purely derived in-memory)
    setTimeout(() => setReleasesLoading(false), 0);
  };

  // ---------- Derivations using pure logic (from local state) ----------
  const movementList: Movement[] = useMemo(
    () => mapAgpToMovements(agpData, batchData, recipeData),
    [agpData, batchData, recipeData],
  );

  const productRows: ProductRow[] = useMemo(
    () => groupMovementsByProduct(movementList, batchData),
    [movementList, batchData],
  );

  const voided = useMemo(() => movementList.filter((m) => m.direction === 'VOID'), [movementList]);

  const releases = useMemo(() => movementList.filter((m) => m.direction === 'OUT'), [movementList]);

  const kpis = useMemo(() => computeKpis(productRows, movementList), [productRows, movementList]);

  const alerts = useMemo(() => computeAlerts(productRows, movementList), [productRows, movementList]);

  const filteredReleases = useMemo(() => {
    const { from, to } = getPeriodBounds(viewMode, activeDate);
    return releases.filter((m) => m.date >= from && m.date < to);
  }, [releases, viewMode, activeDate]);

  const filteredVoided = useMemo(() => {
    const { from, to } = getPeriodBounds(viewMode, activeDate);
    return voided.filter((m) => m.date >= from && m.date < to);
  }, [voided, viewMode, activeDate]);

  const totalExcise = useMemo(
    () => computeTotalExcise(filteredReleases, batchData, recipeData),
    [filteredReleases, batchData, recipeData],
  );

  const releasesWithTotal = useMemo(
    () => [
      ...filteredReleases,
      {
        id: 'total-row',
        amount: 0,
        date: new Date(),
        direction: 'OUT',
        slug: '',
        bottleName: '',
        batchId: '',
        size: 0,
        note: '',
        __isTotal: true,
      } as Movement & { __isTotal?: boolean },
    ],
    [filteredReleases],
  );

  const computeExciseEUR = (m: { size: number; amount: number; batchId: string }) =>
    computeExciseForMovement(m, batchData, recipeData);

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description="Overview of bottles subject to excise control (in stock, recent movements, alerts)."
          actions={
            <SpaceBetween size="m" direction="horizontal">
              {tier >= 3 && (
                <Button iconName="search" variant="normal" onClick={() => navigate('/agpStorageCheck')}>
                  Agp storage check
                </Button>
              )}
              {/* Optional global refresh */}
              {(refetchAll || refetchAgp) && (
                <Button iconName="refresh" variant="normal" onClick={() => void refreshAll()}>
                  Refresh all
                </Button>
              )}
            </SpaceBetween>
          }
        >
          AGP Overview
        </Header>
      }
    >
      <SpaceBetween size="l" direction="vertical">
        {/* KPIs */}
        <Header variant="h2">Key figures</Header>
        <ColumnLayout columns={4} variant="text-grid">
          <Box>
            <Box variant="awsui-value-large">{kpis.totalOnHand}</Box>
            <Box variant="awsui-key-label">Containers on hand</Box>
          </Box>
          <Box>
            <Box variant="awsui-value-large">{kpis.uniqueSkus}</Box>
            <Box variant="awsui-key-label">Products</Box>
          </Box>
          <Box>
            <Box variant="awsui-value-large">{kpis.releases30}</Box>
            <Box variant="awsui-key-label">Released last 30 days</Box>
          </Box>
          <Box>
            <Box variant="awsui-value-large">{kpis.voided30}</Box>
            <Box variant="awsui-key-label">Voided last 30 days</Box>
          </Box>
        </ColumnLayout>

        {/* Alerts */}
        {(alerts.negatives.length > 0 || alerts.staleStock.length > 0) && (
          <Container>
            <Header variant="h2">Alerts</Header>
            <SpaceBetween size="s" direction="vertical">
              {alerts.negatives.length > 0 && (
                <StatusIndicator type="error">Negative stock: {alerts.negatives.join(', ')}</StatusIndicator>
              )}
              {alerts.staleStock.length > 0 && (
                <StatusIndicator type="warning">
                  Very old IN entries (&gt; 180 days): {alerts.staleStock.join(', ')}
                </StatusIndicator>
              )}
            </SpaceBetween>
          </Container>
        )}

        {/* Stock by product */}
        <StockByProductTable
          productRows={productRows}
          onReleaseClick={() => setReleaseModalVisible(true)}
          onVoidClick={() => {
            setVoidModal(true);
            setReleaseModalVisible(true);
          }}
          loading={stockLoading}
          loadingText="Loading stock…"
          refresh={refetchAll || refetchAgp ? () => void refreshStock() : undefined}
        />

        {/* Releases */}
        <ReleasesTable
          releasesWithTotal={releasesWithTotal}
          totalExcise={totalExcise}
          batchMap={batchData}
          computeExciseEUR={computeExciseEUR}
          headerActions={
            <SpaceBetween size="s" direction="horizontal">
              <PeriodFilterControls
                viewMode={viewMode}
                setViewMode={setViewMode}
                activeDate={activeDate}
                handlePeriodChange={handlePeriodChange}
                loading={releasesLoading}
              />
              <Button
                variant="primary"
                onClick={() => setExciseModalVisible(true)}
                disabled={filteredReleases.length === 0}
              >
                Register paid excise
              </Button>
            </SpaceBetween>
          }
          loading={releasesLoading}
          loadingText="Loading releases…"
          refresh={refetchAll || refetchAgp ? () => void refreshReleases() : undefined}
        />

        {/* Recent activity */}
        <RecentMovementsTable
          movements={movementList.slice(0, 10)}
          batchMap={batchData}
          loading={movementsLoading}
          loadingText="Loading activity…"
          refresh={refetchAll || refetchAgp ? () => void refreshMovements() : undefined}
        />
      </SpaceBetween>

      {/* Modals */}

      <ReleaseModal
        visible={releaseModalVisible}
        setVisible={() => {
          setVoidModal(false);
          setReleaseModalVisible(false);
        }}
        batchMap={batchData}
        recipeMap={recipeData}
        isVoid={voidModal}
      />

      <ExcisePaymentModal
        visible={exciseModalVisible}
        onClose={() => setExciseModalVisible(false)}
        items={releasesWithTotal
          .filter((r) => !(r as any).__isTotal)
          .map((r) => ({ id: r.id, amount: r.amount, size: r.size, batchId: r.batchId }))}
        computeExciseEUR={computeExciseEUR}
        defaultDate={new Date().toISOString().slice(0, 10)}
      />
    </ContentLayout>
  );
};

export default AgpOverview;
